// GitHub on the web app (docs/github.md), the parts with no React: the API calls, and which stale PRs
// For you lists. Pure apart from `call`. OEMUAP only: a personal board has no GitHub.

import { isAssignee } from "@boem/core/shared";
import type { BoardItem, BoardPr, BoardReposView, DeployRule, GitHubStatus, ProjectReposView, RepoHit, RepoScanApplied, RepoScanView, UnlinkedPr, Viewer } from "@boem/core/shared";
import { call } from "./api.ts";

const enc = encodeURIComponent;

export const githubApi = {
  /** Settings → GitHub (admins): the connection, deliveries, people */
  settings: () => call<GitHubStatus>("GET", "/api/github/settings"),
  /** Connect: the manifest and where to post it (the browser posts it to GitHub) */
  connect: (owner: string, name: string) => call<{ postUrl: string; manifest: unknown }>("POST", "/api/github/connect", { owner, name }),
  disconnect: () => call("POST", "/api/github/disconnect", {}),
  setStaleDays: (staleDays: number) => call("POST", "/api/github/settings", { staleDays }),
  /** who a PR in review waits for when nobody is requested on GitHub (null: nobody) */
  setDefaultReviewer: (defaultReviewerId: string | null) => call("POST", "/api/github/settings", { defaultReviewerId }),
  setLogin: (memberId: string, login: string | null) => call("PUT", `/api/github/people/${enc(memberId)}`, { login }),
  /** Settings → GitHub's repos (admins and leads), and their facts (admins) */
  boardRepos: () => call<BoardReposView>("GET", "/api/github/board-repos"),
  confirmAutolink: (repoId: string, value: boolean) => call("POST", `/api/github/repos/${enc(repoId)}/autolink`, { value }),
  setDeploy: (repoId: string, rule: DeployRule | null) => call("PUT", `/api/github/repos/${enc(repoId)}/deploy`, { rule }),
  setMainBoard: (repoId: string, value: boolean) => call("PUT", `/api/github/repos/${enc(repoId)}/main-board`, { value }),
  /** a project's repos (leads) */
  searchRepos: (q: string) => call<RepoHit[]>("GET", `/api/github/repos?q=${enc(q)}`),
  projectRepos: (num: number) => call<ProjectReposView>("GET", `/api/github/projects/${num}/repos`),
  addRepo: (num: number, repoId: string) => call("POST", `/api/github/projects/${num}/repos`, { repoId }),
  removeRepo: (num: number, repoId: string) => call("DELETE", `/api/github/projects/${num}/repos/${enc(repoId)}`),
  unlinked: (num: number) => call<UnlinkedPr[]>("GET", `/api/github/projects/${num}/unlinked`),
  /** Scan recent commits: what applying them would change, then Apply; a project's (its items only), or the whole board's (num null, admins) */
  scanRepo: (num: number | null, repoId: string) =>
    call<RepoScanView>("POST", num == null ? `/api/github/repos/${enc(repoId)}/scan` : `/api/github/projects/${num}/repos/${enc(repoId)}/scan`, {}),
  applyScan: (num: number | null, repoId: string, scanId: string) =>
    call<RepoScanApplied>("POST", num == null ? `/api/github/repos/${enc(repoId)}/scan/apply` : `/api/github/projects/${num}/repos/${enc(repoId)}/scan/apply`, { scanId }),
  /** an item's PRs */
  linkPr: (num: number, url: string) => call<{ pr: number; repo: string }>("POST", `/api/github/items/${num}/prs`, { url }),
  unlinkPr: (num: number, prId: string) => call("DELETE", `/api/github/items/${num}/prs/${enc(prId)}`),
};

/** Post the manifest to GitHub, as a form (that's how GitHub's manifest flow takes it): the page goes to GitHub. */
export function postManifest(postUrl: string, manifest: unknown) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = postUrl;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "manifest";
  input.value = JSON.stringify(manifest);
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

/** A stale PR For you shows: on an item you have, or one you opened. */
export interface StalePr {
  item: Pick<BoardItem, "num" | "title" | "key">;
  pr: BoardPr;
}

/** The open items' stale PRs that are the viewer's: the item is with them, or they opened the PR. Pure. */
export function stalePrsFor(items: BoardItem[], viewer: Pick<Viewer, "id" | "name">): StalePr[] {
  const out: StalePr[] = [];
  for (const it of items) {
    if (it.status !== "open" || it.archived) continue;
    for (const pr of it.prs ?? []) if (pr.stale && (isAssignee(it, viewer.id) || pr.author === viewer.name)) out.push({ item: it, pr });
  }
  return out.sort((a, b) => a.pr.lastActivityAt.localeCompare(b.pr.lastActivityAt));
}

/** "oemuap/mycom-next" → "mycom-next", for a chip's tooltip and a row. Pure. */
export const repoShort = (fullName: string) => fullName.split("/").pop() ?? fullName;
